import { AccessTimeRounded, LockOpenRounded, PendingActionsRounded, TouchAppRounded, Widgets } from '@mui/icons-material'
import { IconButton, Stack, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import AlertUI from '../../generic/AlertUI'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import CheckDialog from '../../generic/CheckDialog'
import UnloadActionDialog from './UnloadActionDialog'
import MoveUpIcon from '@mui/icons-material/MoveUp'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@emotion/react'
import RoundedIconButton from '../../Compass/Common/CompassButton/RoundedIconButton'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import useToggleSmartAccordionVisibility from '../../generic/hooks/useToggleSmartAccordionVisibility/useToggleSmartAccordionVisibility'

// The action type TOOL_NOT_AVAILABLE is homonymous with the alarm of the same code (a sentence with {{reason}}).
const ACTION_TYPE_KEYS = { TOOL_NOT_AVAILABLE: 'ACTION_TOOL_NOT_AVAILABLE' }

const Actions = (props) => {
  const [AlertElement, showAlert] = AlertUI()
  const [forceLoad, setForceLoad] = useState(true)
  const [resetMultipleSelectionValue, setResetMultipleSelectionValue] = useState(false)
  const [openUnloadDialog, setOpenUnloadDialog] = useState(false)
  const [showCheckDialog, setShowCheckDialog] = useState(false)
  const [openDialogToConfirmManualActions, setOpenDialogToConfirmManualActions] = useState(false)
  const [selectedAction, setSelectedAction] = useState(null)
  const [result, setResult] = useState(null)
  const theme = useTheme()
  const [alertElementResult, showAlertResult] = AlertUI()
  const [staticConfig] = useFrontEndConfig()
  const smartAccordionVisibility = useToggleSmartAccordionVisibility()

  const { t_ } = useSmartTranslation()
  const navigate = useNavigate()

  const closeCheckDialog = (state) => {
    setShowCheckDialog(state)
  }

  const getIconForType = (type) => {
    if (type == null) {
      return null
    }
    switch (type.code) {
      case 'LOAD':
        return <Widgets color='blue' style={{ color: theme.palette.info.main }} />
      case 'UNLOAD':
        return <MoveUpIcon />
      case 'UNLOCK':
        return <LockOpenRounded />
      default:
        return <AccessTimeRounded style={{ color: '#FFFF00' }} />
    }
  }

  const columns = [
    {
      field: 'name',
      search: false,
      render: (row) => (
        row.executionDate === undefined || row.executionDate === null
          ? getIconForType(row.type)
          : null
      )
    },

    {
      name: t_('Code'),
      field: 'code',
      charLimit: 60,
      mandatory: true,
      unique: true
    },
    {
      name: t_('Type'),
      field: 'type.name',
      charLimit: 60,
      mandatory: true,
      unique: true,
      // The action type TOOL_NOT_AVAILABLE shares its code with an alarm sentence: it has a key of its own (P17).
      render: (row) => systemValueLabel(t_, 'action_type', row.type || {}, ACTION_TYPE_KEYS),
      renderOnlyInReadMode: true
    },
    {
      name: t_('Description'),
      field: 'description',
      charLimit: 200,
      readOnly: true

    },
    {
      name: t_('Creation date'),
      field: 'createdDate',
      type: 'datetime',
      readOnly: true,
      sortOptions: 'asc'
    },
    {
      name: t_('Reading date'),
      readOnly: true,
      field: 'readingDate',
      type: 'datetime'
    }
  ]

  const sortOptions = {
    default: {
      field: 'createdDate',
      direction: -1
    }
  }
  const onClickIconButton = (row) => {
    setResult(null)
    setSelectedAction(row)
    if (row.type.code === 'UNLOAD') {
      setOpenUnloadDialog(true)
    } else if (row.type && row.type.code === 'PLAN') {
      navigate('/planning')
    } else {
      setOpenDialogToConfirmManualActions(true)
    }
  }
  const actions = {
    custom: [
      {
        name: 'Check action',
        contextMenu: false,
        showOnNew: 'false',
        render: (row) => {
          return (
            <IconButton onClick={() => onClickIconButton(row)}>
              <TouchAppRounded sx={{ fontSize: '2rem', filter: 'drop-shadow(2px 1px black)' }} />
            </IconButton>
          )
        }
      }
    ],

    accordion: {
      render: (row) => (
        <SmartMateriaUITable
          columns={columns}
          dataFetch={(pagination) => fetchSubActions(pagination, row.id)}
          actions={actions}
          sortOptions={sortOptions}
          forceLoad={forceLoad}
          deleteMultipleSelectionDisabled
          resetMultipleSelectionValue={resetMultipleSelectionValue}
          pageable
        />
      ),
      visibilityButton: (row) => {
        return row.hasChilds
      }
    }

  }

  const fetchData = useCallback(async (pagination) => {
    let dataList = []
    dataList = await apiGet('uncheckedActions', pagination)
      .catch((error) => {
        showAlert({
          title: 'Error getting data.',
          message: error
        })
      })
    if (dataList.content !== undefined && dataList.content.find((data) => data.readingDate === null)) {
      await apiPut('updateReadingDate', dataList.content)
      setForceLoad(!forceLoad)
    }

    return dataList
  }, [showAlert, forceLoad])

  const fetchSubActions = useCallback(async (pagination, id) => {
    let dataList = []
    dataList = await apiGet('uncheckedActions/' + id, pagination)
      .catch((error) => {
        showAlert({
          title: 'Error getting data.',
          message: error
        })
      })
    if (dataList.content !== undefined && dataList.content.find((data) => data.readingDate === null)) {
      await apiPut('updateReadingDate', dataList.content)
      setForceLoad(!forceLoad)
    }

    return dataList
  }, [showAlert, forceLoad])

  const updateExecutedDate = async () => {
    await apiPut('updateExecutedDate/' + selectedAction.id + (result !== null ? '/' + result : ''))
      .then((data) => {
        showAlert({
          title: 'Updated correctly.',
          severity: 'success'
        })
        setResetMultipleSelectionValue(!resetMultipleSelectionValue)
      }
      )
      .catch((error) => {
        showAlert({
          title: 'Error updating data.',
          message: error.message,
          severity: 'error'
        })
      })
    setForceLoad(!forceLoad)
  }

  const titleDialogManualAction = {
    icon: <PendingActionsRounded fontSize='large' color='warning' />,
    render: t_('Action complete?')
  }

  const confirmDialogContent = () => {
    if (!selectedAction) {
      return
    }
    if (selectedAction.type.code === 'EXECUTE' || selectedAction.type.code === 'PENDING_VALIDATION') {
      return (
        <>
          {alertElementResult}
          <Stack gap={2}>

            <Typography>{t_('Select the result of the operation')}</Typography>
            <Stack sx={{ width: '100%' }} direction='row' gap={3} justifyContent='center'>
              <RoundedIconButton
                selected={result && result === 'FAIL'}
                backgroundColor='#FF0000'
                icon={<CloseIcon style={{ transform: ' scale(2.2)' }} />}
                onClick={() => { setResult('FAIL') }}
              />
              <RoundedIconButton
                selected={result && result === 'OK'}
                backgroundColor='green'
                icon={<CheckIcon style={{ transform: ' scale(2.2)' }} />}
                onClick={() => { setResult('OK') }}
              />
            </Stack>
          </Stack>
        </>
      )
    }
    return (
      <SmartMateriaUITable
        columns={columns}
        dataFetch={[selectedAction]}
        title={t_('Actions')}
        searchAndPaginationVisible=''
        pageable={false}
      />
    )
  }

  const acceptManualActions = () => {
    updateExecutedDate(selectedAction.id)
    setOpenDialogToConfirmManualActions(false)
  }

  return (
    <>
      {AlertElement}
      <UnloadActionDialog
        action={selectedAction}
        open={openUnloadDialog}
        key={'unload-action-dialog-id-' + (selectedAction ? selectedAction.id : '')}
        setOpen={setOpenUnloadDialog}
        onDone={() => {
          showAlert({
            title: 'Updated correctly.',
            severity: 'success'
          })
        }}
      />
      <SmartDialog
        message={t_("Are you sure you want to set action as complete? You can't undo this action.")}
        renderComponent={confirmDialogContent()}
        cancelCallback={() => setOpenDialogToConfirmManualActions(false)}
        setOpen={openDialogToConfirmManualActions}
        title={titleDialogManualAction}
        accept
        cancel
        acceptCallback={() => {
          if (selectedAction.type.code === 'EXECUTE' && !result) {
            showAlertResult(
              {
                message: t_('Please select the result'),
                severity: 'error'
              })
            return
          }

          // CheckDialog decides which steps apply based on the cell state and the REQUIRE_PASSWORD setting.
          setShowCheckDialog(true)
          setOpenDialogToConfirmManualActions(false)
        }}
      />
      <CheckDialog
        open={showCheckDialog}
        onClose={(state) => closeCheckDialog(state)}
        onAccept={() => {
          acceptManualActions()
          setShowCheckDialog(false)
        }}
        fixtureUse={staticConfig?.fixtureUse}
      />
      <SmartMateriaUITable
        columns={columns}
        dataFetch={fetchData}
        actions={actions}
        sortOptions={sortOptions}
        forceLoad={forceLoad}
        deleteMultipleSelectionDisabled
        resetMultipleSelectionValue={resetMultipleSelectionValue}
        accordionVisibility={smartAccordionVisibility}
        pageable
        toolbar={props.toolbar}
      />
    </>
  )
}

export default Actions
